import { queryOptions, useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useTranslation, type Language } from '@/shared/i18n'
import { isObjectId } from '@/shared/lib'
import { giftApi } from '../api/gift-api'
import { isSendToken } from '../lib/start-param'
import { isGiftTakenError } from './errors'
import {
  forgetOfflineVoucher,
  forgetOfflineVoucherBySend,
  readOfflineVoucher,
  readOfflineVoucherBySend,
  syncOfflineVoucher,
} from './offline-vouchers'
import type { GiftOpening, MyGiftsFilter } from './types'

/**
 * Язык — часть ключа: название продукта, инструкцию и условия бэкенд отдаёт
 * уже переведёнными по `Accept-Language`.
 *
 * Два входа в подарок — два ключа: по id (`detail`) и по токену отправки
 * (`send`, BE-53). Ответ по токену с карточкой кладётся и в `detail` её
 * подарка (`primeGiftDetail`): экраны решения и ваучера живут на id и
 * рисуются без второго запроса.
 */
export const giftKeys = {
  all: ['gifts'] as const,
  details: () => [...giftKeys.all, 'detail'] as const,
  detail: (giftId: string, language: Language) => [...giftKeys.details(), giftId, language] as const,
  sends: () => [...giftKeys.all, 'send'] as const,
  send: (token: string, language: Language) => [...giftKeys.sends(), token, language] as const,
  /** Счётчики «Моих подарков» — без языка: в них только числа. */
  counts: () => [...giftKeys.all, 'counts'] as const,
  lists: () => [...giftKeys.all, 'list'] as const,
  /** Роль и статусы — часть ключа: у каждой вкладки и фильтра свой кэш, переключение без мигания. */
  list: ({ role, statuses }: MyGiftsFilter, language: Language) =>
    [...giftKeys.lists(), { role, statuses: statuses ? statuses.join(',') : null, language }] as const,
}

/** Подарков на страницу — как строк витрины. */
export const MY_GIFTS_PAGE_SIZE = 20

/**
 * «Мои подарки» и «История» постранично (BE-54, BE-55): следующая страница —
 * когда список доезжает до конца (`widgets/gift-list`).
 *
 * Статусы меняются чужими действиями (получатель открыл и принял, касса
 * погасила), а фонового опроса нет (CLAUDE.md, «Правила поллинга»): список
 * перечитывается при каждом входе на экран и при возврате в приложение
 * (`staleTime: 0`), а до ответа виден прошлый — из кэша, без скелетона.
 *
 * Счётчики обеих ролей приходят с каждой страницей — они же кладутся в кэш
 * профиля (`useMyGiftCountsQuery`): бейдж и статистика сходятся со списком.
 */
export const useMyGiftsQuery = (filter: MyGiftsFilter) => {
  const { language } = useTranslation()
  const queryClient = useQueryClient()

  return useInfiniteQuery({
    queryKey: giftKeys.list(filter, language),
    queryFn: async ({ pageParam, signal }) => {
      const page = await giftApi.list(filter, { limit: MY_GIFTS_PAGE_SIZE, skip: pageParam }, signal)

      if (page.counts) {
        queryClient.setQueryData(giftKeys.counts(), page.counts)
      }

      return page
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((count, page) => count + page.items.length, 0)

      // Пустая страница при `loaded < total` — список сжался между запросами; дальше не идём.
      return lastPage.items.length > 0 && loaded < lastPage.total ? loaded : undefined
    },
    staleTime: 0,
  })
}

/**
 * Счётчики «Моих подарков» для профиля (BE-55). Меняются чужими действиями
 * (получатель принял, касса погасила), поэтому перечитываются при каждом
 * входе в профиль; сбой — профиль просто без статистики.
 */
export const useMyGiftCountsQuery = () =>
  useQuery({
    queryKey: giftKeys.counts(),
    queryFn: ({ signal }) => giftApi.counts(signal),
    staleTime: 0,
  })

/**
 * Подарок по id. `staleTime` короткий: статус меняется чужими действиями
 * (погасили на кассе, вернулся по таймауту), а при возврате в приложение экран
 * перечитывается сам (`refetchOnWindowFocus`). Совсем ноль не ставим: при
 * входе из чата запрос уходит ещё до первого рендера (`app/launch.ts`), и
 * экран не должен тут же повторять его.
 *
 * Из памяти подарок не выбрасывается, пока Mini App открыт (`gcTime` — сутки
 * вместо пяти минут): ушёл с ваучера в витрину и вернулся на кассе без сети —
 * ваучер на месте.
 *
 * После перезапуска действующий ваучер берётся с устройства
 * (`offline-vouchers.ts`): он рисуется сразу — и без сети, и пока бэкенд
 * отвечает, — а запрос всё равно уходит (дата записи — время последнего
 * подтверждения, она всегда «устарела»). Каждый ответ бэкенда обновляет или
 * удаляет запись; язык записи — язык последнего ответа.
 */
export const giftQueryOptions = (giftId: string, language: Language) =>
  queryOptions({
    queryKey: giftKeys.detail(giftId, language),
    queryFn: async ({ signal }): Promise<GiftOpening> => {
      try {
        const opening = await giftApi.get(giftId, signal)

        syncOfflineVoucher(giftId, opening)

        return opening
      } catch (error) {
        if (isGiftTakenError(error)) {
          forgetOfflineVoucher(giftId)
        }

        throw error
      }
    },
    initialData: () => readOfflineVoucher(giftId)?.opening,
    initialDataUpdatedAt: () => readOfflineVoucher(giftId)?.checkedAt,
    staleTime: 5_000,
    gcTime: 24 * 60 * 60_000,
  })

/**
 * Подарок по токену отправки (`startapp=s_<token>`) — без изменений: экран
 * входа решает, коробка это, экран решения, ваучер или «не найден». Те же
 * правила кэша и офлайн-ваучера, что у подарка по id; ваучер, сохранённый на
 * устройстве, находится и по токену.
 */
export const giftSendQueryOptions = (token: string, language: Language) =>
  queryOptions({
    queryKey: giftKeys.send(token, language),
    queryFn: async ({ signal }): Promise<GiftOpening> => {
      const opening = await giftApi.getBySend(token, signal)

      if (opening.gift) {
        syncOfflineVoucher(opening.gift.id, opening, token)
      } else {
        forgetOfflineVoucherBySend(token)
      }

      return opening
    },
    initialData: () => readOfflineVoucherBySend(token)?.opening,
    initialDataUpdatedAt: () => readOfflineVoucherBySend(token)?.checkedAt,
    staleTime: 5_000,
    gcTime: 24 * 60 * 60_000,
  })

/**
 * Ответ с карточкой (по токену, открытие коробки, «Принять») — в кэш подарка
 * по id: следующий экран рисуется сразу, без скелетона и второго запроса.
 */
export const primeGiftDetail = (queryClient: QueryClient, opening: GiftOpening, language: Language): void => {
  if (opening.gift) {
    queryClient.setQueryData(giftKeys.detail(opening.gift.id, language), opening)
  }
}

/**
 * Подарок на текущем языке. Смена языка (в том числе из профиля сразу после
 * старта) перечитывает его, но экран не сбрасывается в загрузку: до ответа
 * остаётся прежний перевод — коробка не должна мигать.
 */
export const useGiftQuery = (giftId: string | undefined) => {
  const { language } = useTranslation()

  return useQuery({
    ...giftQueryOptions(giftId ?? '', language),
    enabled: isObjectId(giftId),
    placeholderData: (previous) => previous,
  })
}

/** Подарок по токену отправки на текущем языке — как `useGiftQuery`. */
export const useGiftSendQuery = (token: string | undefined) => {
  const { language } = useTranslation()

  return useQuery({
    ...giftSendQueryOptions(token ?? '', language),
    enabled: isSendToken(token),
    placeholderData: (previous) => previous,
  })
}
